import {
	arrow,
	autoUpdate,
	flip,
	offset,
	shift,

	computePosition,

	Placement
} from "@floating-ui/dom";

import "./Tooltip.js";
import {waitUntil} from "../../main/utils/async.js";
import Account from "../../main/account.js";
import Data from "../../main/data/index.js";

type tooltipTextFunction = (elem: HTMLElement) => any;

interface tooltipOptions {
	forceStickyTooltip?: boolean;

	overflowingClass?:      string;
	showOnlyIfOverflowing?: boolean;

	tooltipPosition?: Placement;
	tooltipText?:     string | tooltipTextFunction;
}

interface TooltipInstanceParams {
	isSticky:              boolean;
	overflowingClass:      string;
	referenceElement:      HTMLElement;
	showOnlyIfOverflowing: boolean;
	tooltipPosition?:      Placement;
	tooltipText:           string | tooltipTextFunction;
}

const IS_STICKY_TOOLTIPS = Account.getPrefsFromAccount(Data.app.me).stickyTooltips;

export class TooltipInstance {
	cleanupAutoUpdate:     Function | null;
	isSticky:              boolean;
	overflowingClass:      string;
	referenceElement:      HTMLElement;
	showOnlyIfOverflowing: boolean;
	tooltipElement:        HTMLElement | null;
	tooltipPosition?:      Placement;
	tooltipText:           string | tooltipTextFunction;

	constructor(params: TooltipInstanceParams) {
		const {
			isSticky,
			overflowingClass,
			referenceElement,
			showOnlyIfOverflowing,
			tooltipPosition,
			tooltipText
		} = params;

		this.isSticky = isSticky;
		this.overflowingClass = overflowingClass;
		this.referenceElement = referenceElement;
		this.showOnlyIfOverflowing = showOnlyIfOverflowing;
		this.tooltipPosition = tooltipPosition;
		this.tooltipText = tooltipText;

		// TODO: Make a decent sticky tooltip
		// let preventHide = false;

		this.init();
	}

	init = () => {
		// Add event handlers on the reference element to show and hide the tooltip
		[
			["mouseenter", this.showTooltip],
			["mouseleave", this.hideTooltip],
			["focus", this.showTooltip],
			["blur", this.hideTooltip]
			// @ts-expect-error FIXME
		].forEach(([event, listener]: [string, EventListener]) => {
			// Remove first, so that if attachTooltip() is called on the same
			// reference element with different options, the new options are used.
			this.referenceElement.removeEventListener(event, listener);
			this.referenceElement.addEventListener(event, listener);
		});
	};

	updatePosition = async () => {
		if (!this.tooltipElement || !this.referenceElement)
			return;

		// Get the arrow element
		let arrowElement: HTMLElement | null | undefined;

		try {
			// TODO: can this be better? it's waiting for things inside the
			// shadowRoot to exist in the DOM
			await waitUntil(() => {
				arrowElement = this.tooltipElement?.shadowRoot?.querySelector(".tooltip-arrow");
				return !!arrowElement;
			}, 50, 1000);
		} catch (error) {
			// Arrow element not found in time
			return;
		}

		// Get positioning information
		const {x, y, placement, strategy,
			middlewareData: {arrow: {x: arrowX, y: arrowY} = {}}
		} = await computePosition(this.referenceElement, this.tooltipElement, {
			middleware: [
				// Docs: offset() should generally be placed at the beginning of
				// your middleware array.
				offset(9),
				flip({fallbackStrategy: "initialPlacement"}),
				shift({padding: 5}),

				// Docs: arrow() should generally be placed toward the end of your
				// middleware array, after shift() (if used).
				arrow({element: arrowElement!})
			],
			placement: this.tooltipPosition
		});

		// Position the tooltip
		Object.assign(this.tooltipElement.style, {
			position: strategy,
			left:     `${x}px`,
			top:      `${y}px`,
			opacity:  1
		});

		// Position the arrow
		const staticSide = {
			top:    "bottom",
			right:  "left",
			bottom: "top",
			left:   "right"
		} [placement.split("-")[0]] ?? this.tooltipPosition;

		Object.assign(arrowElement!.style, {
			top:           arrowY != null ? `${arrowY}px` : "",
			left:          arrowX != null ? `${arrowX}px` : "",
			right:         "",
			bottom:        "",
			[staticSide!]: "-4px"
		});
	};

	createTooltip = () => {
		this.tooltipElement = document.createElement("pn-tooltip");
		this.tooltipElement.innerHTML = this.getTooltipText();

		return this.tooltipElement;
	};

	/* TODO: Make a decent sticky tooltip
	const mousemoveHandler = (event) => {
		const refRect = referenceElement.getBoundingClientRect();
		const tooltipRect = tooltipElement.getBoundingClientRect();

		const {x: mouseX, y: mouseY} = event;

		// Is the ref higher on the screen, or the tooltip?
		const isRefHigher = refRect.top < tooltipRect.top;

		// Is the ref more to the left, or the tooltip?
		const isRefLefter = refRect.left < tooltipRect.left;

		const isHoveringRef
			= (mouseX >= refRect.top && mouseX <= refRect.bottom)
			&& (mouseY >= refRect.left && mouseY <= refRect.right);

		// Just draw a rectangle for now between the two things..
		const minX = Math.min(refRect.x, tooltipRect.x);
		const maxX = Math.max(refRect.right, tooltipRect.right);
		const minY = Math.min(refRect.y, tooltipRect.y);
		const maxY = Math.max(refRect.bottom, tooltipRect.bottom);

		const isXWithinBounds = mouseX >= minX && mouseX <= maxX;
		const isYWithinBounds = mouseY >= minY && mouseY <= maxY;

		preventHide = isXWithinBounds && isYWithinBounds;

		if (!preventHide) {
			hideTooltip();
			document.removeEventListener("mousemove", mousemoveHandler);
		}
	};
	*/

	addStickyFunctionality = (event: PointerEvent | FocusEvent) => {
		// Dismiss tooltip on esc
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape")
				this.hideTooltip();
			document.removeEventListener("keydown", onKeyDown);
		};

		this.referenceElement.addEventListener("keydown", onKeyDown);

		// Keep the tooltip open when you hover it
		if (!(event instanceof MouseEvent))
			return;

		// TODO: Make a decent sticky tooltip
		// document.addEventListener("mousemove", mousemoveHandler);
	};

	showTooltip = (event?: PointerEvent | FocusEvent) => {
		// This tooltip already exists in the DOM
		if (this.tooltipElement)
			return;

		// Showing on overflow only
		if (this.showOnlyIfOverflowing) {
			let refElem = this.referenceElement;

			// If an overflowing class was specified, look for it within the reference
			if (this.overflowingClass !== "" && !refElem.classList.contains(this.overflowingClass))
				refElem = refElem.querySelector(`.${this.overflowingClass}`) ?? refElem;

			// Not overflowing
			if (refElem.offsetWidth >= refElem.scrollWidth)
				return;
		}

		// Create the tooltip and position it
		this.tooltipElement = this.createTooltip();
		this.updatePosition();

		// Add tooltip to DOM
		document.body.append(this.tooltipElement);

		if (this.isSticky && event)
			this.addStickyFunctionality(event);

		// Automatically update the tooltip position when things move
		this.cleanupAutoUpdate = autoUpdate(
			this.referenceElement,
			this.tooltipElement,
			this.updatePosition
		);
	};

	hideTooltip = () => {
		// TODO: Make a decent sticky tooltip
		// if (preventHide)
		// 	return;

		// Remove tooltip from DOM
		this.tooltipElement?.remove();
		this.tooltipElement = null;

		// Remove any stickiness
		// TODO: Make a decent sticky tooltip
		// document.removeEventListener("mousemove", mousemoveHandler);

		// Stop updates
		if (this.cleanupAutoUpdate) {
			this.cleanupAutoUpdate();
			this.cleanupAutoUpdate = null;
		}
	};

	getTooltipText = () => {
		let result = (typeof this.tooltipText === "string")
			? this.tooltipText
			: this.tooltipText(this.referenceElement);

		if (result === "") {
			const tippyText = this.referenceElement.getAttribute("data-tippy-content");
			if (tippyText !== null)
				result = tippyText;
		}

		return result;
	};

	updateTooltipText = (tooltipText: string | tooltipTextFunction) => {
		if (tooltipText === this.tooltipText)
			return;

		this.hideTooltip();
		this.tooltipText = tooltipText;

		this.showTooltip();
	};
};

/**
 * Creates and returns one or multiple ```TooltipInstance```s, depending on how
 * many reference elements are passed in.
 *
 * @todo Param changes are not fully handled (only ```tooltipText``` is handled)
 * @todo Sticky tooltips implementation
 */
export function attachTooltip(reference: HTMLElement, params?: tooltipOptions): TooltipInstance;
export function attachTooltip(reference: HTMLElement[] | NodeList, params?: tooltipOptions): TooltipInstance[];
export function attachTooltip(reference: HTMLElement | HTMLElement[] | NodeList, params?: tooltipOptions): TooltipInstance | TooltipInstance[] {
	const options: tooltipOptions = params ?? {};

	const {
		// TODO: see pn-www tooltips
		forceStickyTooltip = false,

		/***
		 * These two props allow the tooltip to be shown only if what's displayed is
		 * overflowing. Useful for letting users see the full text in the tooltip.
		 */
		overflowingClass = "",
		showOnlyIfOverflowing = false,

		tooltipPosition = "bottom",
		// This can also be a function, especially in the case of multiple tooltips
		tooltipText = ""
	} = options;

	const isSticky = IS_STICKY_TOOLTIPS ?? forceStickyTooltip;

	// Adds a tooltip to an individual element
	const initTooltip = (referenceElement: HTMLElement) => {
		const tooltipInstance = new TooltipInstance({
			referenceElement,
			isSticky,
			overflowingClass,
			showOnlyIfOverflowing,
			tooltipPosition,
			tooltipText
		});

		return tooltipInstance;
	};

	if (reference instanceof HTMLElement)
		return initTooltip(reference);
	else {
		// Initialize a tooltip for each element in the array or NodeList
		return Array.from(reference).map(elem => initTooltip(elem as HTMLElement));
	}
};

// TODO:
// -> Sticky tooltips: see floating-ui react safePolygon() for inspiration
// -> tooltipNowrap
// -> topics menu tooltips
